/**
 * threadReceiptsRepository — read receipts for shared threads (2026-09-08, PR-3).
 *
 * Store: `group_thread_messages/{threadId}/receipts/{readerId}` where the
 * reader is a participant uid (client-written, OWN doc only — firestore.rules)
 * or `'assistant'` (server-written by the durable-write path when Prest0n
 * answers; never a uid). One doc per reader, a high-water mark:
 *   { lastReadOrderTs: int, lastReadMessageId?: string, updatedAt: serverTimestamp }
 *
 * Writes are MONOTONIC (rules refuse a decrease; the client never asks for
 * one), debounced 500 ms per thread so a dwell that fires as messages keep
 * landing coalesces into one write, and best-effort: a refusal (a thread whose
 * registry predates the floor backfill, a member whose add is still settling)
 * is swallowed — a receipt is a courtesy to the others, never a gate on
 * anything the viewer does.
 *
 * Reads are one collection listener per open thread with the same fail-closed
 * flag and the same retry ladder as the shared-message overlay
 * (groupThreadMessagesRepository): flag off → no Firestore access, a no-op
 * detach; an error backs off and re-attaches.
 */
import { onSnapshot } from 'firebase/firestore';
import {
  assertCurrentUid,
  collection,
  db,
  debouncedSetDoc,
  doc,
  serverTimestamp,
} from './firestoreUtils';
import { resolveGroupThreadReadEnabled } from '../services/groupThreads/featureFlags';
import { GROUP_LISTENER_RETRY_DELAYS_MS } from './groupThreadMessagesRepository';
import { ASSISTANT_RECEIPT_ID, type ReceiptFacts, type ReceiptsByReader } from '../services/groupThreads/seenBy';

const GROUP_STORE = 'group_thread_messages';
const RECEIPTS = 'receipts';

export const RECEIPT_WRITE_DEBOUNCE_MS = 500;

/** Local monotonic guard: threadId|uid → the highest mark this session wrote. */
const lastWrittenMark = new Map<string, number>();

/** Test seam. */
export function resetThreadReceiptsRepositoryForTests(): void {
  lastWrittenMark.clear();
}

export function receiptDocRef(threadId: string, readerId: string) {
  return doc(db, GROUP_STORE, threadId, RECEIPTS, readerId);
}

export interface WriteOwnReceiptOptions {
  /** Resolve the account-scoped flag against this account instead of the mount-published one. */
  accountId?: string | null;
}

/**
 * Write the viewer's own receipt. Resolves true when the mark advanced and
 * the write settled on the server; false when skipped (flag off, invalid,
 * not an advance) or refused.
 */
export async function writeOwnReceipt(
  threadId: string,
  uid: string,
  facts: ReceiptFacts,
  opts?: WriteOwnReceiptOptions,
): Promise<boolean> {
  const id = (threadId ?? '').trim();
  const reader = (uid ?? '').trim();
  if (!id || !reader || reader === ASSISTANT_RECEIPT_ID) return false;
  if (!resolveGroupThreadReadEnabled(opts?.accountId ? { accountId: opts.accountId } : {})) return false;
  const mark = facts?.lastReadOrderTs;
  if (typeof mark !== 'number' || !Number.isFinite(mark) || mark < 0) return false;
  const key = `${id}|${reader}`;
  const prev = lastWrittenMark.get(key);
  if (prev !== undefined && mark <= prev) return false;
  try {
    assertCurrentUid(reader, 'writeOwnReceipt');
  } catch {
    return false;
  }
  const data: Record<string, unknown> = {
    lastReadOrderTs: Math.floor(mark),
    updatedAt: serverTimestamp(),
  };
  const msgId = typeof facts.lastReadMessageId === 'string' ? facts.lastReadMessageId.trim() : '';
  if (msgId) data.lastReadMessageId = msgId;
  const ref = receiptDocRef(id, reader);
  try {
    await debouncedSetDoc(ref, data, { debounceKey: ref.path, debounceMs: RECEIPT_WRITE_DEBOUNCE_MS });
    const nowPrev = lastWrittenMark.get(key);
    if (nowPrev === undefined || mark > nowPrev) lastWrittenMark.set(key, Math.floor(mark));
    return true;
  } catch {
    return false; // best-effort: a refused receipt never surfaces
  }
}

/** Parse one receipts snapshot into the reader map; malformed docs are dropped. */
export function parseReceiptsSnapshot(
  docs: ReadonlyArray<{ id: string; data: () => unknown }>,
): ReceiptsByReader {
  const out: ReceiptsByReader = {};
  for (const d of docs) {
    const reader = (d?.id ?? '').trim();
    if (!reader) continue;
    const raw = (typeof d.data === 'function' ? d.data() : null) as Record<string, unknown> | null;
    const mark = raw?.lastReadOrderTs;
    if (typeof mark !== 'number' || !Number.isFinite(mark) || mark < 0) continue;
    const facts: ReceiptFacts = { lastReadOrderTs: Math.floor(mark) };
    const msgId = raw?.lastReadMessageId;
    if (typeof msgId === 'string' && msgId.trim()) facts.lastReadMessageId = msgId.trim();
    out[reader] = facts;
  }
  return out;
}

/**
 * Listen to a thread's receipts. Every snapshot — including an empty one — is
 * handed on, so a reader map goes back to {} when the store says nobody has
 * read anything (unlike the message overlay, absence here is information).
 */
export function subscribeThreadReceipts(
  threadId: string,
  onReceipts: (receipts: ReceiptsByReader) => void,
  opts?: { accountId?: string | null },
): () => void {
  if (!resolveGroupThreadReadEnabled(opts?.accountId ? { accountId: opts.accountId } : {})) return () => {};
  const id = (threadId ?? '').trim();
  if (!id) return () => {};

  let stopped = false;
  let detachInner: (() => void) | null = null;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;
  let retryIndex = 0;

  const scheduleRetry = (): void => {
    if (stopped) return;
    const delay = GROUP_LISTENER_RETRY_DELAYS_MS[
      Math.min(retryIndex, GROUP_LISTENER_RETRY_DELAYS_MS.length - 1)
    ];
    retryIndex += 1;
    retryTimer = setTimeout(attach, delay);
  };

  const attach = (): void => {
    if (stopped) return;
    try {
      const unsubscribe = onSnapshot(
        collection(db, GROUP_STORE, id, RECEIPTS),
        (snap) => {
          retryIndex = 0;
          onReceipts(parseReceiptsSnapshot(snap.docs));
        },
        () => {
          try { unsubscribe(); } catch { /* already detached */ }
          scheduleRetry();
        },
      );
      detachInner = unsubscribe;
    } catch {
      scheduleRetry();
    }
  };

  attach();

  return () => {
    stopped = true;
    if (retryTimer !== null) clearTimeout(retryTimer);
    try { detachInner?.(); } catch { /* already detached */ }
  };
}
